import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import {
  ArrowLeft,
  Bookmark,
  Building2,
  Check,
  Globe2,
  Instagram,
  Plus,
  Search,
  Sparkles,
  Star,
  TriangleAlert,
  Users,
  X,
} from "lucide-react";

import {
  Avatar,
  Badge,
  Button,
  Card,
  Dialog,
  Input,
  ScrollArea,
  StatBlock,
  Tabs,
} from "@/components/ui";
import {
  getAriClientAvatarUrl,
  useAriWikiImages,
} from "@/hooks/portfolio/useAriWikiImages";
import {
  buildAriRosterClients,
  getAriRosterTotals,
} from "@/mockData/ariPortfolio";
import type {
  AriHubTab,
  AriOperationsSubTab,
  AriOverviewSubTab,
  AriRosterClient,
} from "@/types/ariPortfolio.types";
import { PATH_ROOT } from "@/routes/paths";
import { cn } from "@/utils/Functions";

import { AriClientFinancialsPanel } from "./AriClientFinancialsPanel";
import { ClientNotesPanel } from "./ClientNotesPanel";

const ROSTER_FILTERS = ["All", "Music", "Sports"] as const;
type RosterFilter = (typeof ROSTER_FILTERS)[number];

const ROSTER_CARD_MOTION = {
  layout: { type: "spring" as const, stiffness: 320, damping: 32 },
  opacity: { duration: 0.18 },
  scale: { duration: 0.18 },
};

const HUB_TABS: { id: AriHubTab; label: string }[] = [
  { id: "overview", label: "Overview" },
  { id: "financials", label: "Financials" },
  { id: "operations", label: "Operations" },
];

const OVERVIEW_SUB_TABS: { id: AriOverviewSubTab; label: string }[] = [
  { id: "profile", label: "Profile" },
  { id: "notes", label: "Notes" },
  { id: "compliance", label: "Compliance" },
];

const OPERATIONS_SUB_TABS: { id: AriOperationsSubTab; label: string }[] = [
  { id: "banking", label: "Banking" },
  { id: "company", label: "Company & Tax" },
  { id: "team", label: "Team" },
];

export default function AriDemoRosterPage() {
  const navigate = useNavigate();
  const clients = useMemo(() => buildAriRosterClients(), []);

  return (
    <RosterGrid
      clients={clients}
      onSelect={(id) => navigate(PATH_ROOT.demo.rosterClient(id))}
    />
  );
}

function RosterGrid({
  clients,
  onSelect,
}: {
  clients: AriRosterClient[];
  onSelect: (id: string) => void;
}) {
  const wikiImages = useAriWikiImages();
  const [filter, setFilter] = useState<RosterFilter>("All");
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const totals = useMemo(() => getAriRosterTotals(clients), [clients]);

  const filtered = useMemo(() => {
    return clients.filter((client) => {
      if (filter !== "All" && client.category !== filter) return false;
      if (search && !client.name.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [clients, filter, search]);

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <section className="shrink-0 border-b border-border">
        <div className="flex items-start justify-between gap-4 px-5 py-4">
          <div className="flex flex-col gap-1">
            <h1 className="text-xl font-semibold">Roster</h1>
            <p className="text-sm text-muted-foreground">Click any client to open their management hub.</p>
          </div>
          <div className="mt-0 flex flex-wrap gap-6">
            <StatBlock label="Clients" value={clients.length} align="left" valueClassName="text-2xl" />
            <StatBlock
              label="Total commission earned"
              value={Math.round(totals.totalCommission)}
              prefix="$"
              suffix="M+"
              align="left"
              valueClassName="text-2xl"
            />
            <StatBlock
              label="Client career value"
              value={totals.totalEarnings}
              prefix="$"
              suffix="M+"
              align="left"
              valueClassName="text-2xl text-success"
            />
          </div>
        </div>
      </section>

      <header className="shrink-0 border-b border-border bg-card/40 px-5 py-2.5">
        <div className="flex flex-wrap items-center gap-2">
          {ROSTER_FILTERS.map((cat) => (
            <Button
              key={cat}
              size="sm"
              variant={filter === cat ? "default" : "outline"}
              className="rounded-full"
              onClick={() => setFilter(cat)}
            >
              {cat}
            </Button>
          ))}
          <Button size="sm" className="gap-1.5" onClick={() => setAddOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Add Client
          </Button>
          <div className="relative ml-auto min-w-[180px] flex-1 sm:max-w-xs">
            <Search
              className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search roster…"
              className="pl-8"
              aria-label="Search roster"
            />
          </div>
        </div>
      </header>

      <ScrollArea className="flex-1">
        <div className="p-6">
          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-16 text-center">
              <Users className="mb-3 size-10 text-muted-foreground" aria-hidden="true" />
              <p className="text-sm font-medium">No clients match your search</p>
              <p className="mt-1 text-xs text-muted-foreground">Try a different filter or search term.</p>
            </div>
          ) : (
            <LayoutGroup id="roster-grid">
              <motion.div
                layout
                className="grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4"
              >
                <AnimatePresence mode="popLayout" initial={false}>
                  {filtered.map((client) => (
                    <motion.div
                      key={client.id}
                      layout
                      layoutId={`roster-client-${client.id}`}
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.92 }}
                      transition={ROSTER_CARD_MOTION}
                    >
                      <ClientCard
                        client={client}
                        avatarUrl={getAriClientAvatarUrl(client.id, wikiImages, client.avatarUrl)}
                        onOpen={() => onSelect(client.id)}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </motion.div>
            </LayoutGroup>
          )}
        </div>
      </ScrollArea>

      <AddClientDialog open={addOpen} onOpenChange={setAddOpen} />
    </div>
  );
}

function ClientCard({
  client,
  avatarUrl,
  onOpen,
}: {
  client: AriRosterClient;
  avatarUrl: string;
  onOpen: () => void;
}) {
  return (
    <Card className="group gap-0 overflow-hidden p-0 shadow-sm transition-opacity hover:opacity-90">
      {/* <div className="relative h-24 shrink-0 overflow-hidden bg-muted" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(135deg, ${client.categoryColor}55 0%, ${client.categoryColor}22 50%, transparent 100%)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/5 via-background/25 to-background/50 backdrop-blur-md" />
      </div> */}

      <div className="">
        <div className="p-3 bg-muted">
        <div className="flex gap-3">
          <div className="relative size-16 shrink-0 overflow-hidden rounded-full border-4 border-card bg-muted">
            <img
              src={avatarUrl}
              alt={client.displayName}
              className="size-full object-cover"
            />
          </div>
          <div className="flex min-w-0 flex-1 items-start justify-between gap-2 self-end">
            <div className="min-w-0">
              <p className="truncate text-base font-semibold">{client.displayName}</p>
              <p className="truncate text-sm text-muted-foreground">{client.title}</p>
            </div>
            <button
              type="button"
              className="grid size-9 shrink-0 place-items-center rounded-full border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={`Bookmark ${client.displayName}`}
              onClick={(event) => event.stopPropagation()}
            >
              <Bookmark className="size-4" aria-hidden="true" />
            </button>
          </div>
        </div>
        

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-xs font-medium">
            <span
              className="size-2 rounded-full"
              style={{ backgroundColor: client.categoryColor }}
              aria-hidden="true"
            />
            {client.category}
          </span>
          {client.activeDeals > 0 && (
            <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground">
              +{client.activeDeals}
            </span>
          )}
        </div>

        </div>

        <div className="grid grid-cols-3 divide-x divide-border border-y border-border py-3">
          <RosterStatCell
            value={
              <span className="inline-flex items-center gap-1">
                <Star className="size-3 fill-current text-muted-foreground" aria-hidden="true" />
                {client.relationshipScore}
              </span>
            }
            label="score"
          />
          <RosterStatCell value={`$${client.totalEarnings}M+`} label="career value" />
          <RosterStatCell value={`$${Math.round(client.totalCommission)}M`} label="commission" />
        </div>
        
        <div className="p-3">
        <Button
          type="button"
          variant="secondary"
          className="w-full rounded-full"
          onClick={onOpen}
        >
          Open Hub
        </Button>
        </div> 
      </div>
    </Card>
  );
}

function RosterStatCell({
  value,
  label,
}: {
  value: ReactNode;
  label: string;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5 px-1 text-center">
      <p className="text-sm font-semibold tabular-nums">{value}</p>
      <p className="text-[10px] text-muted-foreground">{label}</p>
    </div>
  );
}

export function ClientHub({ client, onBack }: { client: AriRosterClient; onBack: () => void }) {
  const navigate = useNavigate();
  const wikiImages = useAriWikiImages();
  const avatarUrl = getAriClientAvatarUrl(client.id, wikiImages, client.avatarUrl);
  const [tab, setTab] = useState<AriHubTab>("overview");
  const [overviewSubTab, setOverviewSubTab] = useState<AriOverviewSubTab>("profile");
  const [operationsSubTab, setOperationsSubTab] = useState<AriOperationsSubTab>("banking");

  const otherClients = useMemo(
    () => buildAriRosterClients().filter((item) => item.id !== client.id),
    [client.id],
  );

  const kpis = [
    { label: "Career Value", value: client.totalEarnings, prefix: "$", suffix: "M+" },
    {
      label: "My Commission",
      value: Math.round(client.totalCommission),
      prefix: "$",
      suffix: "M",
    },
    {
      label: "Commission Rate",
      value: Math.round(client.commissionRate * 100),
      suffix: "%",
    },
    { label: "Total Deals", value: client.pastDeals.length },
    { label: "Active Deals", value: client.activeDeals },
    {
      label: "Rel. Score",
      value: client.relationshipScore,
      suffix: "/100",
    },
  ];

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 px-6 pt-5">
        <Button
          variant="ghost"
          size="sm"
          className="-ml-2 gap-1.5 text-muted-foreground"
          onClick={onBack}
        >
          <ArrowLeft className="size-4" aria-hidden="true" />
          Roster
        </Button>
      </div>

      <div className="shrink-0 border-b border-border px-6 pt-4 pb-6">
        <div className="flex items-center gap-5">
          <div className="size-20 shrink-0 overflow-hidden rounded-full ring-1 ring-border">
            <img
              src={avatarUrl}
              alt={client.name}
              className="size-full object-cover"
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <h1 className="truncate text-2xl font-semibold">{client.name}</h1>
              <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                <span
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: client.categoryColor }}
                  aria-hidden="true"
                />
                {client.category}
              </span>
            </div>
            <p className="mt-1 truncate text-sm text-muted-foreground">{client.companyName}</p>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          {kpis.map((kpi) => (
            <div
              key={kpi.label}
              className="rounded-xl bg-muted/50 p-4"
            >
              <StatBlock
                label={kpi.label}
                value={kpi.value}
                prefix={kpi.prefix}
                suffix={kpi.suffix}
                replayKey={client.id}
                align="left"
                valueClassName="text-xl text-foreground"
              />
            </div>
          ))}
        </div>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as AriHubTab)} className="flex min-h-0 flex-1 flex-col">
        <Tabs.List variant="underline" className="w-full shrink-0 justify-start gap-0 overflow-x-auto px-6">
          {HUB_TABS.map((hubTab) => (
            <Tabs.Trigger key={hubTab.id} value={hubTab.id} className="px-3.5 capitalize">
              {hubTab.label}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <ScrollArea className="flex-1">
          <Tabs.Panel value="overview" className="p-6">
            <Tabs
              value={overviewSubTab}
              onValueChange={(value) => setOverviewSubTab(value as AriOverviewSubTab)}
            >
              <Tabs.List variant="pill" className="mb-4 w-fit" indicatorColor="secondary">
                {OVERVIEW_SUB_TABS.map((subTab) => (
                  <Tabs.Trigger key={subTab.id} value={subTab.id} className="text-xs">
                    {subTab.label}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
              <Tabs.Panel value="profile">
                <ProfileTab client={client} />
                <OtherClientsSection
                  clients={otherClients}
                  wikiImages={wikiImages}
                  onSelect={(id) => navigate(PATH_ROOT.demo.rosterClient(id))}
                />
              </Tabs.Panel>
              <Tabs.Panel value="notes">
                <ClientNotesPanel clientId={client.id} />
              </Tabs.Panel>
              <Tabs.Panel value="compliance">
                <ComplianceTab client={client} />
              </Tabs.Panel>
            </Tabs>
          </Tabs.Panel>
          <Tabs.Panel value="financials" className="p-6">
            <AriClientFinancialsPanel clientId={client.id} />
          </Tabs.Panel>
          <Tabs.Panel value="operations" className="p-6">
            <Tabs
              value={operationsSubTab}
              onValueChange={(value) => setOperationsSubTab(value as AriOperationsSubTab)}
            >
              <Tabs.List variant="pill" className="mb-4 w-fit" indicatorColor="secondary">
                {OPERATIONS_SUB_TABS.map((subTab) => (
                  <Tabs.Trigger key={subTab.id} value={subTab.id} className="text-xs">
                    {subTab.label}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
              <Tabs.Panel value="banking">
                <BankingTab client={client} />
              </Tabs.Panel>
              <Tabs.Panel value="company">
                <CompanyTaxTab client={client} />
              </Tabs.Panel>
              <Tabs.Panel value="team">
                <TeamTab client={client} />
              </Tabs.Panel>
            </Tabs>
          </Tabs.Panel>
        </ScrollArea>
      </Tabs>
    </div>
  );
}

function OtherClientsSection({
  clients,
  wikiImages,
  onSelect,
}: {
  clients: AriRosterClient[];
  wikiImages: Record<string, string>;
  onSelect: (id: string) => void;
}) {
  if (clients.length === 0) return null;

  return (
    <div className="mt-8 border-t border-border pt-6">
      <SectionLabel>Other clients</SectionLabel>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {clients.map((other) => (
          <button
            key={other.id}
            type="button"
            onClick={() => onSelect(other.id)}
            className="group flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-4 text-center transition-colors hover:bg-muted"
          >
            <div className="size-14 overflow-hidden rounded-full ring-1 ring-border">
              <img
                src={getAriClientAvatarUrl(other.id, wikiImages, other.avatarUrl)}
                alt={other.displayName}
                className="size-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{other.displayName}</p>
              <p className="mt-0.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                <span
                  className="size-1.5 rounded-full"
                  style={{ backgroundColor: other.categoryColor }}
                  aria-hidden="true"
                />
                {other.category}
              </p>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ProfileTab({ client }: { client: AriRosterClient }) {
  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <SectionLabel>Bio</SectionLabel>
        <p className="text-pretty text-sm leading-7 text-muted-foreground">{client.bio}</p>
      </div>
      {client.social && (
        <div>
          <SectionLabel>Links</SectionLabel>
          <div className="flex flex-wrap gap-2">
            {client.social.wikipedia && (
              <SocialLink href={client.social.wikipedia} icon={<Globe2 className="size-3.5" />} label="Wikipedia" />
            )}
            {client.social.instagram && (
              <SocialLink href={client.social.instagram} icon={<Instagram className="size-3.5" />} label="Instagram" />
            )}
            {client.social.spotify && (
              <SocialLink href={client.social.spotify} icon={<Sparkles className="size-3.5" />} label="Spotify" />
            )}
          </div>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Card className="gap-1 p-4">
          <p className="text-xs text-muted-foreground">Outreach</p>
          <p className="text-sm font-medium">{client.outreachNote}</p>
        </Card>
        <Card className="gap-1 p-4">
          <p className="text-xs text-muted-foreground">Relationship score</p>
          <p className="text-sm font-medium">{client.relationshipScore}/100</p>
        </Card>
      </div>
    </div>
  );
}

function ComplianceTab({ client }: { client: AriRosterClient }) {
  const compliance = [
    ["LLC Filed", true],
    ["W-9 on File", Boolean(client.biz.tax.w9Status)],
    [
      "NDA Signed",
      client.pastDeals.some((deal) => deal.status === "Signed" || deal.status === "Closed"),
    ],
    ["Direct Deposit", client.relationshipScore > 80],
  ] as const;

  return (
    <div className="max-w-md">
      <SectionLabel>Compliance</SectionLabel>
      <Card className="gap-0 p-0">
        <Card.Panel className="divide-y divide-border p-0">
          {compliance.map(([label, ok]) => (
            <div key={label} className="flex items-center justify-between px-3.5 py-2.5 text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className={cn("font-semibold", ok ? "text-success" : "text-destructive")}>
                {ok ? <Check className="size-4" aria-label="Complete" /> : <X className="size-4" aria-label="Incomplete" />}
              </span>
            </div>
          ))}
        </Card.Panel>
      </Card>
    </div>
  );
}

function BankingTab({ client }: { client: AriRosterClient }) {
  const { banking } = client.biz;
  const rows = [
    ["Routing Number", banking.routing],
    ["Account Number", banking.account],
    ["Wire Transfer", banking.wire],
    ["Account Type", banking.type],
  ] as const;

  return (
    <div className="max-w-xl space-y-4">
      <Card>
        <Card.Panel className="space-y-4 p-5">
          <div className="flex items-center gap-3">
            <div className="grid size-11 place-items-center rounded-lg bg-primary/10 text-lg">🏦</div>
            <div>
              <p className="font-medium">{banking.bank}</p>
              <p className="text-sm text-muted-foreground">{banking.type}</p>
            </div>
            <Badge className="ml-auto bg-success/10 text-success">{banking.status}</Badge>
          </div>
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-center justify-between border-t border-border py-3 text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-mono font-medium">{value}</span>
            </div>
          ))}
        </Card.Panel>
      </Card>
      <div className="flex gap-2.5 rounded-xl bg-warning/5 px-4 py-3">
        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-warning-foreground" aria-hidden="true" />
        <p className="text-sm leading-6 text-warning-foreground">
          Banking details are encrypted and access-controlled. Only authorized team members can view full account numbers.
        </p>
      </div>
    </div>
  );
}

function CompanyTaxTab({ client }: { client: AriRosterClient }) {
  const { company, tax } = client.biz;
  const companyRows = [
    ["EIN", company.ein],
    ["State", company.state],
    ["Founded", company.formed],
    ["Address", company.address],
    ["Officers", company.officers],
  ] as const;

  return (
    <div className="flex max-w-3xl flex-col gap-5 lg:flex-row">
      <div className="min-w-0 flex-1">
        <SectionLabel>Company Information</SectionLabel>
        <Card>
          <Card.Panel className="space-y-4 p-5">
            <div className="flex items-center gap-3">
              <div className="grid size-10 place-items-center rounded-lg bg-primary/10">
                <Building2 className="size-5 text-primary" aria-hidden="true" />
              </div>
              <div>
                <p className="font-medium">{company.name}</p>
                <p className="text-sm text-muted-foreground">{company.type}</p>
              </div>
            </div>
            {companyRows.map(([label, value]) => (
              <div key={label} className="flex gap-3 border-t border-border pt-3 text-sm">
                <span className="w-24 shrink-0 text-muted-foreground">{label}</span>
                <span className="font-medium">{value}</span>
              </div>
            ))}
          </Card.Panel>
        </Card>
      </div>
      <div className="w-full shrink-0 lg:w-60">
        <SectionLabel>Tax Information</SectionLabel>
        <Card>
          <Card.Panel className="divide-y divide-border p-0">
            {[
              ["W-9 Status", tax.w9Status],
              ["Tax Filing", tax.filingStatus],
              ["Tax ID", tax.taxId],
              ["Last Filed", tax.lastFiled],
              ["Next Due", tax.nextDue],
            ].map(([label, value]) => (
              <div key={label} className="flex items-center justify-between px-4 py-2.5 text-sm">
                <span className="text-muted-foreground">{label}</span>
                <span className="font-medium">{value}</span>
              </div>
            ))}
          </Card.Panel>
        </Card>
      </div>
    </div>
  );
}

function TeamTab({ client }: { client: AriRosterClient }) {
  const sections = [
    { side: "client" as const, title: "Client's Team" },
    { side: "agency" as const, title: "Our Team (Equal IQ)" },
  ];

  return (
    <div className="grid max-w-3xl gap-5 md:grid-cols-2">
      {sections.map(({ side, title }) => {
        const members = client.teamMembers.filter((member) => member.side === side);
        return (
          <div key={side}>
            <SectionLabel>
              {title} ({members.length})
            </SectionLabel>
            <div className="space-y-2">
              {members.map((member) => (
                <Card key={member.name} className="gap-0 p-0">
                  <Card.Panel className="flex items-center gap-3 p-3">
                    <Avatar
                      className={cn(
                        "size-9 font-semibold text-white",
                        side === "agency" ? "bg-primary" : "bg-success",
                      )}
                    >
                      {member.name[0]}
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium">{member.name}</p>
                      <p className="text-xs text-muted-foreground">{member.role}</p>
                    </div>
                  </Card.Panel>
                </Card>
              ))}
              {members.length === 0 && (
                <p className="text-sm text-muted-foreground">No team members on file.</p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function SocialLink({
  href,
  label,
  icon,
}: {
  href: string;
  label: string;
  icon: React.ReactNode;
}) {
  return (
    <Button variant="outline" size="sm" className="gap-1.5" render={<a href={href} target="_blank" rel="noreferrer" />}>
      {icon}
      {label}
    </Button>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 text-[11px] font-semibold text-muted-foreground">{children}</p>
  );
}

function AddClientDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Popup className="max-w-md">
        <Dialog.Header>
          <Dialog.Title>Add New Client</Dialog.Title>
          <Dialog.Description>
            Demo roster is pre-loaded. Full client creation will connect to your CRM in production.
          </Dialog.Description>
        </Dialog.Header>
        <Dialog.Footer>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </Dialog.Footer>
      </Dialog.Popup>
    </Dialog>
  );
}
